import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Caption from '@zenbusiness/common-components/Caption';
import SharedAsset from '@zenbusiness/common-components/SharedAsset';
import Text from '@zenbusiness/common-components/Text';
import format from 'date-fns/format';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';

const messages = {
  labels: {
    title: 'ZenBusiness Domain',
    externalTitle: 'Domain',
    status: 'Status',
    configured: 'Setup',
    connection: 'Connection',
    privacy: 'Privacy',
    activeUtil: 'Renewal Date',
    button: 'Domain Settings'
  }
};

const StyledCardRoot = styled(Card)(({ theme }) => ({
  width: '100%',
  borderRadius: theme.spacing(2)
}));

const StyledCardActionArea = styled(CardActionArea)(() => ({
  height: '100%'
}));

const StyledTextDomainName = styled(Text)(() => ({
  '&.MuiTypography-root': {
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(0, 4)
}));

const StyledCardActions = styled(CardActions)(({ theme }) => ({
  flexDirection: 'column-reverse',
  padding: theme.spacing(0, 4, 3, 4)
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  paddingBottom: theme.spacing(2.5),
  paddingTop: theme.spacing(2.5),
  display: 'block',
  lineHeight: theme.spacing(3),
  fontWeight: 700,
  color: theme.palette.peace40.main,
  position: 'static',

  '&:before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%'
  }
}));

const StyledSharedAssetIconShield = styled(SharedAsset)(({ theme }) => ({
  marginLeft: theme.spacing(0.6),
  fill: theme.palette.primary.light,
  stroke: theme.palette.primary.light
}));

const StyledSharedAssetIconAlert = styled(SharedAsset)(({ theme }) => ({
  marginLeft: theme.spacing(0.6),
  fill: theme.palette.error.light
}));

const StyledBoxCardProperties = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0, 0, 0),
  height: 'auto'
}));

const StyledSharedAssetCardIconLarge = styled(SharedAsset)(({ theme }) => ({
  display: 'block',
  margin: 'auto',
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(0.6)
}));

const StyledSpanComboProp = styled('span')(() => ({
  display: 'flex',
  alignItems: 'center'
}));

const DomainCardProperty = ({ label, children }) => (
  <Grid container spacing={1} alignItems="center">
    <Grid item xs alignItems="center">
      <Caption size="lg" align="right">
        {label}
        :
      </Caption>
    </Grid>
    <Grid item xs alignItems="center">
      <Caption size="lg" component="span">
        {children}
      </Caption>
    </Grid>
  </Grid>
);
DomainCardProperty.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

const DomainCard = ({ domain }) => {
  const { businessEntityUuid } = useBusinessEntity();
  const { domainUuid, isExternal } = domain;

  const websiteConnectionStatus = domain?.websiteConnectionStatus;
  const unconnected = websiteConnectionStatus === null;
  const connected = websiteConnectionStatus === 'connected';
  const disconnected = websiteConnectionStatus === 'disconnected';
  const connecting = websiteConnectionStatus === 'ready_for_verification';

  const disabled = (domain.expired || domain.transferred);
  const errorMessages = [];
  const pending = !domain.verified && !domain.isExternal;
  const domainSettingsLink = `/d/company/${businessEntityUuid}/marketing-tools/domains/${domain.domainUuid}`;

  const handleSettingsClick = useLinkPressedCallback({
    elementName: 'domain_settings',
    elementText: 'Domain Settings',
    linkDestination: domainSettingsLink,
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domains_page'
  });

  if (domain.expired) {
    errorMessages.push('This domain has expired.');
  } else if (domain.transferred) {
    errorMessages.push('This domain was transferred.');
  } else if (!domain.verified && !domain.isExternal) {
    errorMessages.push('This domain is pending verification.');
    errorMessages.push('Check your email for verification instructions.');
  } else if (isExternal && disconnected) {
    errorMessages.push('The connection between your external domain and ZenBusiness website could not be made.');
  }

  return (
    <StyledCardRoot variant="outlined" key={domainUuid}>
      <StyledCardActionArea component={Box}>
        <StyledSharedAssetCardIconLarge width={80} src="/icon/misc/domain.svg" />
        <StyledCardContent>
          <Text size="sm" align="center">
            {isExternal ? messages.labels.externalTitle : messages.labels.title}
          </Text>
          <StyledTextDomainName size="md" gutterBottom align="center">
            {domain.domainName}
          </StyledTextDomainName>
          <StyledBoxCardProperties>
            <DomainCardProperty label={messages.labels.status}>
              {domain.active ? 'Active' : 'Inactive'}
            </DomainCardProperty>
            {!isExternal && (
              <DomainCardProperty label={messages.labels.configured}>
                {domain.configured ? 'Yes' : 'No'}
              </DomainCardProperty>
            )}
            {isExternal && (
              <DomainCardProperty label={messages.labels.connection}>
                {connected && 'Connected'}
                {connecting && 'Pending'}
                {disconnected && 'Disconnected'}
                {unconnected && 'Unconnected'}
              </DomainCardProperty>
            )}

            {!isExternal && (
              <DomainCardProperty label={messages.labels.privacy}>
                {domain.domainPrivacy ? (
                  <StyledSpanComboProp>
                    On
                    <StyledSharedAssetIconShield src="/icon/micro/shield.svg" width={18} alt="shield" title="Domain privacy one" />
                  </StyledSpanComboProp>
                ) : (
                  <StyledSpanComboProp>
                    Off
                    <StyledSharedAssetIconAlert src="/icon/micro/alert.svg" width={16} alt="alert" title="Domain privacy off" />
                  </StyledSpanComboProp>
                )}
              </DomainCardProperty>
            )}
            {domain.srsExpireDatetime && (
              <DomainCardProperty label={messages.labels.activeUtil}>
                {format(new Date(domain.srsExpireDatetime), 'MM/dd/yyyy')}
              </DomainCardProperty>
            )}
          </StyledBoxCardProperties>
        </StyledCardContent>
        <StyledCardActions>
          {errorMessages.length === 0 && (
            <StyledButton
              color="primary"
              component={RouterLink}
              disabled={disabled || pending}
              onClick={handleSettingsClick}
              size="medium"
              to={domainSettingsLink}
              variant="text"
            >
              {messages.labels.button}
            </StyledButton>
          )}
          {errorMessages.length > 0 && (
            <div>
              {errorMessages.map((message) => (
                <Text key={`${domain.domainUuid}-${message}`} size="sm" color="energyBrand" align="center">
                  {message}
                </Text>
              ))}
            </div>
          )}
        </StyledCardActions>
      </StyledCardActionArea>
    </StyledCardRoot>
  );
};

DomainCard.propTypes = {
  domain: PropTypes.shape({
    domainUuid: PropTypes.string,
    domainName: PropTypes.string,
    active: PropTypes.bool,
    configured: PropTypes.bool,
    domainPrivacy: PropTypes.bool,
    transferred: PropTypes.bool,
    expired: PropTypes.bool,
    verified: PropTypes.bool,
    srsExpireDatetime: PropTypes.string,
    isExternal: PropTypes.bool,
    websiteConnectionStatus: PropTypes.string
  }).isRequired
};
export default DomainCard;
